import type { AvatarColor, AvatarId, Mood } from '@crew/shared';

const PALETTE: Record<AvatarColor, [string, string]> = {
  pink: ['#ff3d9a', '#ffa3d1'],
  coral: ['#ff5a2b', '#ffab8c'],
  amber: ['#ffbe2e', '#ffdd94'],
  yellow: ['#f2ea3d', '#fbf6a0'],
  lime: ['#a4e82c', '#d6f79b'],
  green: ['#28d95f', '#93f0ad'],
  teal: ['#12b5a3', '#84e2d8'],
  cyan: ['#1fd0f5', '#99e8fb'],
  blue: ['#3763ff', '#9aaeff'],
  indigo: ['#5b3bd6', '#a998f0'],
  violet: ['#a94dff', '#d6a6ff'],
  magenta: ['#ff2fd0', '#ff9fe9']
};

/**
 * Crew portraits: a helmet silhouette (the face) tinted by a chosen colour.
 * Drawn as SVG so they stay crisp at any size, add no network weight, and are
 * original artwork with no licensing attached.
 */
export function Avatar({
  id,
  color = 'blue',
  mood = 'happy',
  size = 34,
  className
}: {
  id: AvatarId;
  color?: AvatarColor;
  mood?: Mood;
  size?: number;
  className?: string;
}) {
  const [base, light] = PALETTE[color] ?? PALETTE.blue;
  const gid = `av-${id}-${color}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      className={className}
      role="img"
      aria-label={`${color} ${id} portrait`}
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={light} />
          <stop offset="100%" stopColor={base} />
        </linearGradient>
        <radialGradient id={`${gid}-glow`} cx="50%" cy="34%" r="60%">
          <stop offset="0%" stopColor={light} stopOpacity="0.5" />
          <stop offset="100%" stopColor={base} stopOpacity="0" />
        </radialGradient>
      </defs>

      <circle cx="32" cy="32" r="31" fill="#080d1a" />
      <circle cx="32" cy="32" r="31" fill={`url(#${gid}-glow)`} />

      <rect x="26" y="38" width="12" height="13" rx="4.5" fill={base} opacity="0.75" />
      <path d="M5 64c0-11 12-17.5 27-17.5S59 53 59 64Z" fill="#16203c" />
      <path d="M5 64c0-11 12-17.5 27-17.5S59 53 59 64Z" fill={base} opacity="0.28" />
      <path d="M23 49c3.5 4.5 14.5 4.5 18 0" stroke={base} strokeWidth="3" fill="none" strokeLinecap="round" />
      <circle cx="14" cy="59" r="2.4" fill={light} />
      <circle cx="50" cy="59" r="2.4" fill={light} />

      <g transform="translate(3.2 -3) scale(0.9)">
        <Helmet id={id} gid={gid} base={base} light={light} mood={mood} />
      </g>
    </svg>
  );
}

function Helmet({
  id,
  gid,
  base,
  light,
  mood
}: {
  id: AvatarId;
  gid: string;
  base: string;
  light: string;
  mood: Mood;
}) {
  const fill = `url(#${gid})`;
  const visor = '#0a1020';

  switch (id) {
    case 'alien':
      return (
        <>
          <path d="M32 8c11 0 18 8 18 18 0 12-8 22-18 30C22 48 14 38 14 26c0-10 7-18 18-18Z" fill={fill} />
          {mood === 'shock' ? (
            <>
              <ellipse cx="24" cy="28" rx="5.4" ry="7.4" fill={visor} transform="rotate(-14 24 28)" />
              <ellipse cx="40" cy="28" rx="5.4" ry="7.4" fill={visor} transform="rotate(14 40 28)" />
              <circle cx="23" cy="26" r="2" fill="#eaf4ff" />
              <circle cx="39" cy="26" r="2" fill="#eaf4ff" />
              <ellipse cx="32" cy="41" rx="3" ry="3.8" fill={visor} />
            </>
          ) : mood === 'wink' ? (
            <>
              <ellipse cx="24" cy="28" rx="4.4" ry="6" fill={visor} transform="rotate(-14 24 28)" />
              <path d="M36 28c2-2.4 4-2.4 6 0" stroke={visor} strokeWidth="2.6" fill="none" strokeLinecap="round" />
              <circle cx="23" cy="26" r="1.5" fill="#eaf4ff" />
              <path d="M27 40c2.6 3.6 7.4 3.6 10 0" stroke={visor} strokeWidth="2.4" fill="none" strokeLinecap="round" />
            </>
          ) : mood === 'grumpy' ? (
            <>
              <ellipse cx="24" cy="28" rx="4.4" ry="6" fill={visor} transform="rotate(-30 24 28)" />
              <ellipse cx="40" cy="28" rx="4.4" ry="6" fill={visor} transform="rotate(30 40 28)" />
              <path d="M27 43c2.6-3.6 7.4-3.6 10 0" stroke={visor} strokeWidth="2.4" fill="none" strokeLinecap="round" />
            </>
          ) : mood === 'cool' ? (
            <>
              <rect x="18" y="24" width="28" height="7" rx="2.5" fill={visor} />
              <path d="M27 41c2.6 2.4 7.4 2.4 10 0" stroke={visor} strokeWidth="2.4" fill="none" strokeLinecap="round" />
            </>
          ) : (
            <>
              <ellipse cx="24" cy="28" rx="4.4" ry="6" fill={visor} transform="rotate(-14 24 28)" />
              <ellipse cx="40" cy="28" rx="4.4" ry="6" fill={visor} transform="rotate(14 40 28)" />
              <circle cx="23" cy="26" r="1.5" fill="#eaf4ff" />
              <circle cx="39" cy="26" r="1.5" fill="#eaf4ff" />
              {mood === 'grin' ? (
                <path d="M25 39h14c0 4.4-3.1 6.8-7 6.8s-7-2.4-7-6.8Z" fill={visor} />
              ) : (
                <path d="M27 40c2.6 3.6 7.4 3.6 10 0" stroke={visor} strokeWidth="2.4" fill="none" strokeLinecap="round" />
              )}
            </>
          )}
        </>
      );
    case 'droid':
      return (
        <>
          <rect x="13" y="16" width="38" height="34" rx="9" fill={fill} />
          <rect x="19" y="25" width="26" height="13" rx="6" fill={visor} />
          {mood === 'shock' ? (
            <>
              <circle cx="26" cy="31.5" r="4" fill={light} />
              <circle cx="38" cy="31.5" r="4" fill={light} />
            </>
          ) : mood === 'wink' ? (
            <>
              <circle cx="26" cy="31.5" r="3" fill={light} />
              <rect x="34" y="30.5" width="8" height="2.4" rx="1.2" fill={light} />
            </>
          ) : mood === 'grumpy' ? (
            <>
              <path d="M22 29.5 30 32.5M42 29.5 34 32.5" stroke={light} strokeWidth="2.6" strokeLinecap="round" />
            </>
          ) : mood === 'cool' ? (
            <rect x="21" y="29.5" width="22" height="4" rx="2" fill={light} />
          ) : mood === 'grin' ? (
            <>
              <rect x="22" y="28" width="6" height="2.6" rx="1.3" fill={light} />
              <rect x="36" y="28" width="6" height="2.6" rx="1.3" fill={light} />
              <path d="M24 34h16c0 2.6-3.6 4-8 4s-8-1.4-8-4Z" fill={light} />
            </>
          ) : (
            <>
              <circle cx="26" cy="31.5" r="3" fill={light} />
              <circle cx="38" cy="31.5" r="3" fill={light} />
            </>
          )}
          <rect x="30" y="6" width="4" height="9" rx="2" fill={fill} />
          <circle cx="32" cy="5" r="3" fill={light} />
        </>
      );
    case 'captain':
      return (
        <>
          <Dome fill={fill} visor={visor} mood={mood} />
          <path d="M32 3 35.4 10.5 43.5 11.4 37.6 17 39.2 25 32 21.2 24.8 25l1.6-8-5.9-5.6 8.1-.9Z" fill={light} />
        </>
      );
    case 'scanner':
      return (
        <>
          <Dome fill={fill} visor={visor} mood={mood} />
          <rect x="14" y="29" width="36" height="2.5" rx="1.25" fill={light} opacity="0.9" />
          <circle cx="50" cy="30" r="3.4" fill={light} />
        </>
      );
    case 'medic':
      return (
        <>
          <Dome fill={fill} visor={visor} mood={mood} />
          <path d="M29.5 40h5v4h4v5h-4v4h-5v-4h-4v-5h4z" fill={light} />
        </>
      );
    case 'engineer':
      return (
        <>
          <Dome fill={fill} visor={visor} mood={mood} />
          <path
            d="M32 40.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11Zm0 3.4a2.1 2.1 0 1 0 0 4.2 2.1 2.1 0 0 0 0-4.2Z"
            fill={light}
          />
          <path d="M32 38.5v-2M32 55.5v-2M25 46h-2M43 46h-2" stroke={light} strokeWidth="2.4" strokeLinecap="round" />
        </>
      );
    case 'gunner':
      return (
        <>
          <Dome fill={fill} visor={visor} mood={mood} />
          <path d="M16 43h32v5H16z" fill={light} opacity="0.9" />
          <path d="M22 48v6M42 48v6" stroke={light} strokeWidth="3" strokeLinecap="round" />
        </>
      );
    case 'navigator':
      return (
        <>
          <Dome fill={fill} visor={visor} mood={mood} />
          <path d="M32 39l6 13-6-3.4-6 3.4z" fill={light} />
        </>
      );
    case 'mechanic':
      return (
        <>
          <Dome fill={fill} visor={visor} mood={mood} />
          <rect x="23" y="42" width="18" height="5" rx="2.5" fill={light} />
          <circle cx="23" cy="44.5" r="3.4" fill={light} />
        </>
      );
    case 'rookie':
      return (
        <>
          <Dome fill={fill} visor={visor} mood={mood} />
          <circle cx="32" cy="45" r="3.6" fill={light} />
        </>
      );
    case 'astro':
      return (
        <>
          <Dome fill={fill} visor={visor} mood={mood} />
          <path d="M10 30h7M47 30h7" stroke={base} strokeWidth="4" strokeLinecap="round" />
          <circle cx="8" cy="30" r="3" fill={light} />
          <circle cx="56" cy="30" r="3" fill={light} />
        </>
      );
    default:
      return (
        <>
          <Dome fill={fill} visor={visor} mood={mood} />
          <path d="M22 44h20" stroke={light} strokeWidth="3.4" strokeLinecap="round" />
        </>
      );
  }
}

function Dome({ fill, visor, mood = 'happy' }: { fill: string; visor: string; mood?: Mood }) {
  return (
    <>
      <path
        d="M32 9c12.2 0 20.5 8.8 20.5 20.5V40c0 6.4-4.3 10.5-10.5 10.5H22C15.8 50.5 11.5 46.4 11.5 40V29.5C11.5 17.8 19.8 9 32 9Z"
        fill={fill}
      />
      <path
        d="M32 17.5c8.3 0 14.5 5.6 14.5 12.8 0 4.2-2.7 6.9-6.2 6.9H23.7c-3.5 0-6.2-2.7-6.2-6.9 0-7.2 6.2-12.8 14.5-12.8Z"
        fill={visor}
        opacity="0.94"
      />
      <Expression mood={mood} />
      <path d="M23.5 22.5c2.7-1.9 6.2-2.8 9.3-2.6-4.2 1-7.3 3.1-9.3 5.9Z" fill="#fff" opacity="0.45" />
    </>
  );
}

const INK = '#eaf4ff';

function Expression({ mood }: { mood: Mood }) {
  const openEye = (cx: number) => (
    <>
      <circle cx={cx} cy="27.5" r="2.9" fill={INK} />
      <circle cx={cx + 0.9} cy="26.6" r="1" fill="#0a1020" />
    </>
  );

  switch (mood) {
    case 'cool':
      return (
        <>
          <rect x="21" y="24.5" width="22" height="6" rx="2" fill="#0d1526" stroke={INK} strokeWidth="1.4" />
          <path d="M26 33.4h12" stroke={INK} strokeWidth="2.2" strokeLinecap="round" />
        </>
      );
    case 'wink':
      return (
        <>
          {openEye(26.5)}
          <path d="M35 28c1.7-2 3.4-2 5 0" stroke={INK} strokeWidth="2.2" fill="none" strokeLinecap="round" />
          <path d="M26.5 32.6c2.6 3.4 8.4 3.4 11 0" stroke={INK} strokeWidth="2.2" fill="none" strokeLinecap="round" />
        </>
      );
    case 'shock':
      return (
        <>
          <circle cx="26.5" cy="27" r="3.6" fill={INK} />
          <circle cx="37.5" cy="27" r="3.6" fill={INK} />
          <circle cx="26.5" cy="27" r="1.3" fill="#0a1020" />
          <circle cx="37.5" cy="27" r="1.3" fill="#0a1020" />
          <ellipse cx="32" cy="34" rx="2.8" ry="3.4" fill={INK} />
        </>
      );
    case 'grumpy':
      return (
        <>
          {openEye(26.5)}
          {openEye(37.5)}
          <path d="M22.5 22.5 30 25M41.5 22.5 34 25" stroke={INK} strokeWidth="2" strokeLinecap="round" />
          <path d="M26.5 35c2.6-3.4 8.4-3.4 11 0" stroke={INK} strokeWidth="2.2" fill="none" strokeLinecap="round" />
        </>
      );
    case 'grin':
      return (
        <>
          {openEye(26.5)}
          {openEye(37.5)}
          <path d="M25 31.6h14c0 4-3.1 6.2-7 6.2s-7-2.2-7-6.2Z" fill={INK} />
        </>
      );
    default:
      return (
        <>
          {openEye(26.5)}
          {openEye(37.5)}
          <path d="M26.5 32.6c2.6 3.4 8.4 3.4 11 0" stroke={INK} strokeWidth="2.2" fill="none" strokeLinecap="round" />
        </>
      );
  }
}
